/**
 * useCartStore - in-memory cart for desktop billing.
 *
 * Desktop billing cart state:
 *   - In-memory active cart plus held carts for desk-till workflows.
 *   - Promotion and loyalty redemption selections are carried through invoice creation.
 *   - Billing settings are read from useSettingsStore on every recompute.
 *   - Product gstRate / cessRate are used directly at add-time.
 *
 * Recompute model is identical to mobile: every mutation re-runs
 * computeLineItem + computeInvoiceTotals so the cart never drifts.
 */

import { create } from 'zustand';
import {
    BillingConfig,
    ComputedLineItem,
    DEFAULT_BILLING_CONFIG,
    InvoiceTotals,
    PaymentStatus,
    ProductForBilling,
    computeInvoiceTotals,
    computeLineItem,
    derivePaymentStatus,
    toPaisa, LoyaltyConfig } from '../billing/billingEngine';
import { useSettingsStore } from './useSettingsStore';

// ═══════════════════════════════════════════════════════
//  TYPES
// ═══════════════════════════════════════════════════════

export interface CartItem {
    product: ProductForBilling;
    quantity: number;
    unitPrice: number;       // editable
    discountAmount: number;  // item-level discount in rupees
    computed: ComputedLineItem;
}

export type PaymentMethod = 'CASH' | 'UPI' | 'CARD' | 'WALLET' | 'BANK_TRANSFER' | 'CREDIT' | 'CHEQUE';

export interface PaymentEntry {
    method: PaymentMethod;
    amount: number;        // in rupees
    reference?: string;
}

export interface CustomerRef {
    id: string;
    name: string | null;
    phone: string | null;
    gstin: string | null;
    /** Address state — the place-of-supply fallback when there is no GSTIN. */
    state: string | null;
}

/**
 * Snapshot of a cart that has been "parked" — set aside so a new sale can
 * be started without losing this one. Lives in memory only (clears on
 * app restart, same as the active cart).
 */
export interface ParkedCart {
    id: string;
    label: string;
    parkedAt: number;          // epoch ms
    itemCount: number;         // for the chip badge
    totalAmount: number;       // rupees, for the chip subtitle
    snapshot: CartSnapshot;
}

interface CartSnapshot {
    items: CartItem[];
    customer: CustomerRef | null;
    isInterState: boolean;
    globalDiscount: number;
    notes: string;
    appliedPromotionId: string | null;
    appliedPromotionCode: string | null;
    loyaltyPointsRedeemed: number;
    /** Rupee value of that redemption. Points are not rupees once the rate is not 1:1. */
    loyaltyRedemptionRupees: number;
    // payments are intentionally NOT parked — they belong to a settled sale
}

interface CartState {
    // ── Core state ──
    items: CartItem[];
    customer: CustomerRef | null;
    isInterState: boolean;
    globalDiscount: number;     // rupees
    payments: PaymentEntry[];
    notes: string;
    /**
     * Printed "Terms" on the invoice (invoices.terms_conditions). Quick Bill
     * also keeps its dispatch details here — see billing/invoiceExtras.ts.
     */
    terms: string;
    appliedPromotionId: string | null;
    appliedPromotionCode: string | null;
    loyaltyPointsRedeemed: number;
    /** Rupee value of that redemption. Points are not rupees once the rate is not 1:1. */
    loyaltyRedemptionRupees: number;

    // ── Quick Bill ──
    /**
     * Quick Bill mode: typed (non-catalog) items are always allowed and the
     * bill carries a hand-entered date. See billing/billDate.ts.
     */
    quickBill: boolean;
    /**
     * 'YYYY-MM-DD' chosen for the Quick Bill; null means today. Read only
     * while `quickBill` is on, and reset after every bill so the next one is
     * never back-dated by accident.
     */
    billDate: string | null;

    // ── Parked carts ──
    parked: ParkedCart[];

    // ── Transient scan feedback (nonce bumps each scan so repeats still fire) ──
    scanFlash: { productId: string; nonce: number } | null;

    // ── Derived (auto-computed) ──
    totals: InvoiceTotals;
    adjustedItems: ComputedLineItem[];
    paidAmountPaisa: number;
    /** Tendered as CREDIT: debt the customer takes on, not money received. */
    creditAmountPaisa: number;
    paymentStatus: PaymentStatus;
    itemCount: number;

    // ── Actions ──
    addItem: (product: ProductForBilling, qty?: number) => void;
    updateItemQty: (productId: string, qty: number) => void;
    updateItemPrice: (productId: string, price: number) => void;
    updateItemDiscount: (productId: string, discount: number) => void;
    removeItem: (productId: string) => void;
    setCustomer: (customer: CustomerRef | null) => void;
    setIsInterState: (value: boolean) => void;
    setGlobalDiscount: (amount: number) => void;
    setNotes: (notes: string) => void;
    setTerms: (terms: string) => void;
    setAppliedPromotion: (id: string | null, code: string | null, discountAmount: number) => void;
    setLoyaltyRedemption: (points: number, amount: number) => void;
    addPayment: (entry: PaymentEntry) => void;
    removePayment: (index: number) => void;
    clearPayments: () => void;
    clearCart: () => void;
    /** Enter or leave Quick Bill mode. Leaving it forgets the chosen date. */
    setQuickBill: (on: boolean) => void;
    setBillDate: (date: string | null) => void;

    /** Flash + beep signal for a just-scanned product (consumed by CartPanel). */
    signalScan: (productId: string) => void;

    // ── Park / recall ──
    /** Snapshot the current cart, push to `parked`, then clear. Returns the slot id, or `null` if cart is empty. */
    parkCurrent: (label?: string) => string | null;
    /** Restore a parked cart and remove it from `parked`. Caller should confirm if the active cart has items. */
    recallParked: (id: string) => void;
    /** Drop a parked cart without restoring it. */
    discardParked: (id: string) => void;
}

// ═══════════════════════════════════════════════════════
//  EMPTY / RECOMPUTE HELPERS
// ═══════════════════════════════════════════════════════

// Live config reader — pulled from useSettingsStore on every recompute so
// settings changes (TAX_INCLUSIVE / ROUNDING_MODE / COMPOSITION_GST)
// reflect immediately in the cart math without any subscribe plumbing.
/**
 * The store's loyalty policy. Exported so the cart, the checkout and the
 * invoice writer all read one source — the divergence this replaces was three
 * hardcoded rates in three files.
 */
export function getLoyaltyConfig(): LoyaltyConfig {
    const s = useSettingsStore.getState();
    return {
        rupeesPerPoint: s.getNumber('LOYALTY_RUPEES_PER_POINT'),
        pointValueRupees: s.getNumber('LOYALTY_POINT_VALUE'),
    };
}

/**
 * The store's billing convention. Exported so a return resolves it exactly as
 * the sale did — a credit note computed under a different convention refunds a
 * different amount than was charged.
 */
export function getCartConfig(): BillingConfig {
    const s = useSettingsStore.getState();
    const mode = s.getSetting('ROUNDING_MODE');
    const roundingMode: BillingConfig['roundingMode'] =
        mode === 'UP' || mode === 'DOWN' || mode === 'NEAREST' || mode === 'NONE'
            ? mode
            : DEFAULT_BILLING_CONFIG.roundingMode;
    return {
        isTaxInclusive: s.getBoolean('TAX_INCLUSIVE'),
        roundingMode,
        isCompositionDealer: s.getBoolean('COMPOSITION_GST'),
    };
}

const EMPTY_TOTALS: InvoiceTotals = {
    subtotalPaisa: 0, globalDiscountPaisa: 0, taxableAmountPaisa: 0,
    cgstPaisa: 0, sgstPaisa: 0, igstPaisa: 0, cessPaisa: 0,
    totalTaxPaisa: 0, preRoundTotalPaisa: 0, roundOffPaisa: 0, finalTotalPaisa: 0,
    subtotal: 0, globalDiscount: 0, taxableAmount: 0,
    cgst: 0, sgst: 0, igst: 0, cess: 0,
    totalTax: 0, roundOff: 0, totalAmount: 0,
};

function recomputeItems(items: CartItem[], isInterState: boolean): CartItem[] {
    const config = getCartConfig();
    return items.map((item) => ({
        ...item,
        computed: computeLineItem(
            item.product,
            item.quantity,
            item.unitPrice,
            item.discountAmount,
            isInterState,
            config,
        ),
    }));
}

/**
 * Split tenders into money actually received and debt explicitly taken on.
 *
 * A CREDIT tender is the customer promising to pay later — it is not a receipt.
 * Summing it into the paid amount marked the sale fully PAID with no debt
 * recorded anywhere: the customer's balance never moved, the credit limit was
 * never consulted, and every receipts report counted money nobody handed over.
 *
 * `creditAmountPaisa` is what the customer owes on top of any unpaid remainder;
 * the checkout uses paid + credit to decide the sale is fully accounted for.
 */
export function splitTenders(payments: PaymentEntry[]): {
    paidAmountPaisa: number;
    creditAmountPaisa: number;
} {
    let paidAmountPaisa = 0;
    let creditAmountPaisa = 0;
    for (const p of payments) {
        const paisa = toPaisa(p.amount);
        if (p.method === 'CREDIT') creditAmountPaisa += paisa;
        else paidAmountPaisa += paisa;
    }
    return { paidAmountPaisa, creditAmountPaisa };
}

function recomputeDerived(items: CartItem[], globalDiscount: number, payments: PaymentEntry[], isInterState: boolean) {
    const config = getCartConfig();
    const computedItems = items.map((i) => i.computed);
    const { totals, adjustedItems } = computeInvoiceTotals(computedItems, globalDiscount, isInterState, config);
    const { paidAmountPaisa, creditAmountPaisa } = splitTenders(payments);
    const paymentStatus = derivePaymentStatus(totals.finalTotalPaisa, paidAmountPaisa);
    const itemCount = items.reduce((sum, i) => sum + i.quantity, 0);
    return { totals, adjustedItems, paidAmountPaisa, creditAmountPaisa, paymentStatus, itemCount };
}

// ═══════════════════════════════════════════════════════
//  STORE
// ═══════════════════════════════════════════════════════

export const useCartStore = create<CartState>((set, get) => ({
    items: [],
    customer: null,
    isInterState: false,
    globalDiscount: 0,
    payments: [],
    notes: '',
    terms: '',
    appliedPromotionId: null,
    appliedPromotionCode: null,
    loyaltyPointsRedeemed: 0,
    loyaltyRedemptionRupees: 0,
    quickBill: false,
    billDate: null,
    parked: [],
    scanFlash: null,
    totals: EMPTY_TOTALS,
    adjustedItems: [],
    paidAmountPaisa: 0,
    creditAmountPaisa: 0,
    paymentStatus: 'UNPAID',
    itemCount: 0,

    addItem: (product, qty = 1) => {
        const state = get();
        const existing = state.items.find((i) => i.product.id === product.id);

        let newItems: CartItem[];
        if (existing) {
            newItems = state.items.map((item) =>
                item.product.id === product.id
                    ? { ...item, quantity: item.quantity + qty }
                    : item,
            );
        } else {
            const newItem: CartItem = {
                product,
                quantity: qty,
                unitPrice: product.sellingPrice,
                discountAmount: 0,
                computed: computeLineItem(product, qty, product.sellingPrice, 0, state.isInterState, getCartConfig()),
            };
            newItems = [...state.items, newItem];
        }

        newItems = recomputeItems(newItems, state.isInterState);
        const derived = recomputeDerived(newItems, state.globalDiscount, state.payments, state.isInterState);
        set({ items: newItems, ...derived });
    },

    updateItemQty: (productId, qty) => {
        if (qty <= 0) {
            get().removeItem(productId);
            return;
        }
        const state = get();
        let newItems = state.items.map((item) =>
            item.product.id === productId ? { ...item, quantity: qty } : item,
        );
        newItems = recomputeItems(newItems, state.isInterState);
        const derived = recomputeDerived(newItems, state.globalDiscount, state.payments, state.isInterState);
        set({ items: newItems, ...derived });
    },

    updateItemPrice: (productId, price) => {
        const state = get();
        let newItems = state.items.map((item) =>
            item.product.id === productId ? { ...item, unitPrice: Math.max(0, price) } : item,
        );
        newItems = recomputeItems(newItems, state.isInterState);
        const derived = recomputeDerived(newItems, state.globalDiscount, state.payments, state.isInterState);
        set({ items: newItems, ...derived });
    },

    updateItemDiscount: (productId, discount) => {
        const state = get();
        let newItems = state.items.map((item) =>
            item.product.id === productId ? { ...item, discountAmount: Math.max(0, discount) } : item,
        );
        newItems = recomputeItems(newItems, state.isInterState);
        const derived = recomputeDerived(newItems, state.globalDiscount, state.payments, state.isInterState);
        set({ items: newItems, ...derived });
    },

    removeItem: (productId) => {
        const state = get();
        const newItems = state.items.filter((i) => i.product.id !== productId);
        const derived = recomputeDerived(newItems, state.globalDiscount, state.payments, state.isInterState);
        set({ items: newItems, ...derived });
    },

    setCustomer: (customer) => set({ customer }),

    setIsInterState: (value) => {
        const state = get();
        const newItems = recomputeItems(state.items, value);
        const derived = recomputeDerived(newItems, state.globalDiscount, state.payments, value);
        set({ isInterState: value, items: newItems, ...derived });
    },

    setGlobalDiscount: (amount) => {
        const state = get();
        const clamped = Math.max(0, amount);
        const derived = recomputeDerived(state.items, clamped, state.payments, state.isInterState);
        set({ globalDiscount: clamped, ...derived });
    },

    setNotes: (notes) => set({ notes }),
    setTerms: (terms) => set({ terms }),

    setAppliedPromotion: (id, code, discountAmount) => {
        const state = get();
        // The redemption's RUPEE value, not its point count. Adding the count
        // here was a units error that only cancelled out while a point happened
        // to be worth exactly one rupee.
        const discount = Math.max(0, discountAmount) + state.loyaltyRedemptionRupees;
        const derived = recomputeDerived(state.items, discount, state.payments, state.isInterState);
        set({
            appliedPromotionId: id,
            appliedPromotionCode: code,
            globalDiscount: discount,
            ...derived,
        });
    },

    setLoyaltyRedemption: (points, amount) => {
        const state = get();
        const promoDiscount = state.globalDiscount - state.loyaltyRedemptionRupees;
        const discount = Math.max(0, promoDiscount) + Math.max(0, amount);
        const derived = recomputeDerived(state.items, discount, state.payments, state.isInterState);
        set({
            loyaltyPointsRedeemed: Math.max(0, points),
            loyaltyRedemptionRupees: Math.max(0, amount),
            globalDiscount: discount,
            ...derived,
        });
    },

    addPayment: (entry) => {
        const state = get();
        const safeEntry = { ...entry, amount: Math.max(0, entry.amount) };
        const newPayments = [...state.payments, safeEntry];
        const derived = recomputeDerived(state.items, state.globalDiscount, newPayments, state.isInterState);
        set({ payments: newPayments, ...derived });
    },

    removePayment: (index) => {
        const state = get();
        const newPayments = state.payments.filter((_, i) => i !== index);
        const derived = recomputeDerived(state.items, state.globalDiscount, newPayments, state.isInterState);
        set({ payments: newPayments, ...derived });
    },

    clearPayments: () => {
        const state = get();
        const derived = recomputeDerived(state.items, state.globalDiscount, [], state.isInterState);
        set({ payments: [], ...derived });
    },

    clearCart: () => set({
        items: [],
        customer: null,
        isInterState: false,
        globalDiscount: 0,
        payments: [],
        notes: '',
        terms: '',
        appliedPromotionId: null,
        appliedPromotionCode: null,
        loyaltyPointsRedeemed: 0,
    loyaltyRedemptionRupees: 0,
        totals: EMPTY_TOTALS,
        adjustedItems: [],
        paidAmountPaisa: 0,
        creditAmountPaisa: 0,
        paymentStatus: 'UNPAID',
        itemCount: 0,
        // The date goes with the bill it was chosen for. `quickBill` stays on
        // so a run of Quick Bills doesn't need the mode re-entered each time.
        billDate: null,
        // `parked` is intentionally preserved — clearing the active cart
        // must not lose held sales.
    }),

    setQuickBill: (on) => set(on ? { quickBill: true } : { quickBill: false, billDate: null }),
    setBillDate: (date) => set({ billDate: date }),

    signalScan: (productId) =>
        set((s) => ({ scanFlash: { productId, nonce: (s.scanFlash?.nonce ?? 0) + 1 } })),

    parkCurrent: (label) => {
        const state = get();
        if (state.items.length === 0) return null;
        const id = `parked_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
        const slot: ParkedCart = {
            id,
            label: label?.trim() || (state.customer?.name?.trim() || `Cart ${state.parked.length + 1}`),
            parkedAt: Date.now(),
            itemCount: state.itemCount,
            totalAmount: state.totals.totalAmount,
            snapshot: {
                // Defensive deep copies so future mutations to the active
                // cart can never bleed back into a parked snapshot.
                items: state.items.map((it) => ({ ...it, computed: { ...it.computed } })),
                customer: state.customer ? { ...state.customer } : null,
                isInterState: state.isInterState,
                globalDiscount: state.globalDiscount,
                notes: state.notes,
                appliedPromotionId: state.appliedPromotionId,
                appliedPromotionCode: state.appliedPromotionCode,
                loyaltyPointsRedeemed: state.loyaltyPointsRedeemed,
                loyaltyRedemptionRupees: state.loyaltyRedemptionRupees,
            },
        };
        // Push to the front so the most recent park appears first.
        const newParked = [slot, ...state.parked];
        set({
            parked: newParked,
            items: [],
            customer: null,
            isInterState: false,
            globalDiscount: 0,
            appliedPromotionId: null,
            appliedPromotionCode: null,
            loyaltyPointsRedeemed: 0,
    loyaltyRedemptionRupees: 0,
            payments: [],
            notes: '',
            totals: EMPTY_TOTALS,
            adjustedItems: [],
            paidAmountPaisa: 0,
            creditAmountPaisa: 0,
            paymentStatus: 'UNPAID',
            itemCount: 0,
        });
        return id;
    },

    recallParked: (id) => {
        const state = get();
        const slot = state.parked.find((p) => p.id === id);
        if (!slot) return;
        const newParked = state.parked.filter((p) => p.id !== id);
        const snap = slot.snapshot;
        // Recompute everything from the snapshot in case settings (e.g.
        // tax-inclusive, rounding mode) changed while the cart was parked.
        const items = recomputeItems(snap.items, snap.isInterState);
        const derived = recomputeDerived(items, snap.globalDiscount, [], snap.isInterState);
        set({
            parked: newParked,
            items,
            customer: snap.customer,
            isInterState: snap.isInterState,
            globalDiscount: snap.globalDiscount,
            notes: snap.notes,
            payments: [],
            ...derived,
        });
    },

    discardParked: (id) => {
        set({ parked: get().parked.filter((p) => p.id !== id) });
    },
}));
